<template>
  <div
    :class="
      `leaflet-offline-map columns is-gapless ${
        config_.devToolsPosition === 'top' ? 'column-reversed' : ''
      } ${config_.devToolsPosition === 'left' ? 'row-reversed' : ''}`
    "
  >
    <slot name="top" />
    <div
      :class="
        `column ${
          config_.devToolsPosition === 'top' ||
          config_.devToolsPosition === 'bottom'
            ? 'is-12'
            : ''
        }`
      "
    >
      <slot name="insideTop" />
      <div class="map">
        <div
          :id="id"
          :style="`height: ${config_.height}px`"
          v-show="!config_.hidden"
        ></div>
        <div class="progress-bar">
          <progress
            class="progress is-small"
            :value="downloadedTilesCount"
            :max="totalTilesCount"
            v-if="downloading"
            >{{ downloadedTilesCount / totalTilesCount }}%</progress
          >
        </div>
      </div>
      <slot name="insideBottom" />
    </div>
    <slot name="bottom" />
    <div
      :class="
        `column is-${
          config_.devToolsPosition === 'top' ||
          config_.devToolsPosition === 'bottom'
            ? 'is-12'
            : ''
        }`
      "
      v-if="config_.devTools"
    >
      <b-field label="CONFIGURATION OPTIONS"></b-field>
      <div class="columns is-gapless">
        <div class="column is-12">
          <div class="columns is-gapless">
            <div class="column is-6">
              <b-checkbox v-model="configForm.offlineDownload" type="is-dark"
                >Auto-download for Offline</b-checkbox
              >
            </div>
            <div class="column is-6">
              <b-checkbox v-model="configForm.scrollable" type="is-dark"
                >Scrollable</b-checkbox
              >
            </div>
          </div>
        </div>
        <div class="column is-12">
          <b-field label="Map Controls"></b-field>
          <div class="columns is-gapless">
            <div class="column is-4">
              <b-checkbox v-model="configForm.hasDrawControl" type="is-dark"
                >Draw Control</b-checkbox
              >
            </div>
            <div class="column is-4">
              <b-checkbox v-model="configForm.hasOfflineControl" type="is-dark"
                >Offline Control</b-checkbox
              >
            </div>
            <div class="column is-4">
              <b-checkbox v-model="configForm.hasZoomControl" type="is-dark"
                >Zoom Control</b-checkbox
              >
            </div>
            <div class="column is-4">
              <b-checkbox v-model="configForm.hasLocateControl" type="is-dark"
                >Locate Control</b-checkbox
              >
            </div>
            <div class="column is-4">
              <b-checkbox v-model="configForm.hasLayersControl" type="is-dark"
                >Layers Control</b-checkbox
              >
            </div>
          </div>
        </div>
        <div class="column is-12">
          <b-field label="Display on Load"></b-field>
          <div class="columns is-gapless">
            <div
              class="column is-4"
              v-for="style in $options.mapBoxStyles"
              :key="style.label"
            >
              <div class="block">
                <b-radio
                  v-model="configForm.displayOnLoad"
                  :native-value="style.label"
                  type="is-dark"
                  >{{ style.label }}</b-radio
                >
              </div>
            </div>
          </div>
        </div>
        <div class="column is-6">
          <b-field label="Map Center (X)">
            <b-input v-model="configForm.center.lng"></b-input>
          </b-field>
        </div>
        <div class="column is-6">
          <b-field label="Map Center (Y)">
            <b-input v-model="configForm.center.lat"></b-input>
          </b-field>
        </div>
        <div class="column is-6">
          <b-field label="Map Height">
            <b-input v-model="configForm.height"></b-input>
          </b-field>
        </div>
        <div class="column is-6">
          <b-field label="Initial Zoom Level">
            <b-input v-model="configForm.zoom"></b-input>
          </b-field>
        </div>
        <div class="column is-6">
          <b-field label="Minimum Zoom Level">
            <b-input v-model="configForm.minZoom"></b-input>
          </b-field>
        </div>
        <div class="column is-6">
          <b-field label="Maximum Zoom Level">
            <b-input v-model="configForm.maxZoom"></b-input>
          </b-field>
        </div>
        <div class="column is-6">
          <b-field label="Zoom Snap">
            <b-input v-model="configForm.zoomSnap"></b-input>
          </b-field>
        </div>
        <div class="column is-6">
          <b-field label="Extended Bounds (%)">
            <b-input v-model="configForm.extendedBounds"></b-input>
          </b-field>
        </div>
        <div class="column is-6">
          <b-field label='Offline Zoom Levels (",")'>
            <b-input v-model="configForm.offlineZoomLevels"></b-input>
          </b-field>
        </div>
        <div class="column is-12">
          <b-field>
            <p class="control">
              <b-button
                type="is-dark"
                class="is-fullwidth"
                is-fullwidth
                @click="applyConfig"
                >Apply Config</b-button
              >
            </p>
          </b-field>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet.offline";
import "leaflet-simple-map-screenshoter";
// Temporarily use this package until the official leaflet.vectorgrid releases the new updates
import "@bagage/leaflet.vectorgrid";
import offlineStorage from "leaflet.offline/src/localforage";

export default {
  mapBoxUrl: `https://api.mapbox.com/styles/v1/sitevitals/{id}/tiles/256/{z}/{x}/{y}?access_token=${process.env.MAPBOX_ACCESS_TOKEN}`,
  mapBoxAttribution:
    'Map data &copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors, <a href="https://creativecommons.org/licenses/by-sa/2.0/">CC-BY-SA</a>, Imagery © <a href="https://www.mapbox.com/">Mapbox</a>',
  mapBoxStyles: [
    {
      id: "cjz0sp88060a11cmd41hoq5te",
      label: "Streets"
    },
    {
      id: "cjz0t3cce18t91co6mhpx4w3n",
      label: "Satellite"
    }
  ],
  vectorGridStyles: {
    active: {
      color: "#DC143C",
      fillColor: "#DC143C",
      weight: 2,
      radius: 3,
      fill: true,
      fillOpacity: 0.7
    },
    default: {
      color: "#3498DB",
      fillColor: "#3498DB",
      weight: 2,
      radius: 3,
      fill: true,
      fillOpacity: 0.7
    }
  },
  colors: {
    active: "#DC143C",
    circle: "#FF00FF",
    line: "#B71C1C",
    polygon: "#263238",
    marker: "#3498DB"
  },
  tileSize: 256,
  props: {
    id: {
      type: String,
      default: "map"
    },
    config: {
      type: Object,
      default: () => {}
    },
    markers: {
      type: Array,
      default: () => []
    },
    lines: {
      type: Array,
      default: () => []
    },
    polygons: {
      type: Array,
      default: () => []
    },
    circles: {
      type: Array,
      default: () => []
    }
  },
  data: () => ({
    map: null,
    previousActive: {
      obj: null,
      colorAttr: null
    },
    me: {
      marker: null,
      radius: null
    },
    // Multipurpose map objects
    flexible: {
      marker: null
    },
    layers: {
      main: null,
      me: null,
      markers: null,
      circles: null,
      lines: null,
      polygons: null,
      vectorGrids: []
    },
    icons: {},
    mapScreenshoter: null,
    drawControl: null,
    drawToggleControl: null,
    offlineControl: null,
    locateControl: null,
    layersControl: null,
    pendingTileRequests: [],
    pendingTilesCount: 0, // tiles that are not yet downloaded
    downloadedTilesCount: 0,
    totalTilesCount: 0,
    downloading: false,
    tileLayers: {},
    config_: {
      hidden: false,
      center: {
        lat: -36.858411,
        lng: 174.760959
      },
      height: 500,
      zoom: 17,
      zoomSnap: 1,
      minZoom: 0,
      maxZoom: 19,
      extendedBounds: 5,
      displayOnLoad: "",
      offlineDownload: true,
      scrollable: true,
      scrollWheelZoom: true,
      clickable: true, // If markers, lines, polygons, circles can be clicked
      devTools: false,
      devToolsPosition: "left",
      hasDrawControl: false,
      hasDrawToggleControl: false,
      hasOfflineControl: true,
      hasZoomControl: true,
      hasLocateControl: true,
      hasLayersControl: true,
      hasCustomControl: false,
      customControlIcon: "fa fa-smile-o",
      customControlPosition: "bottomleft",
      markerOnClick: false
    },
    configForm: {
      center: {}
    },
    currentZoom: 17,
    currentTileLayer: "",
    icon: null,
    drawing: false,
    loadedMarkers: [],
    loadedLines: [],
    loadedPolygons: [],
    loadedCircles: [],
    extendedBounds: null,
    // Vector Grid
    activeFeature: null,
    activeFeaturePopup: null,
    hoverFeaturePopup: null
  }),
  computed: {
    defaultTileLayer() {
      return this.tileLayers[this.config_.displayOnLoad];
    },
    offlineZoomLevels() {
      if (this.configForm.offlineZoomLevels) {
        let zoomLevels = this.configForm.offlineZoomLevels.split(",");

        zoomLevels = zoomLevels.map(zoomLevel => parseInt(zoomLevel));

        if (!zoomLevels.includes(this.currentZoom)) {
          zoomLevels.push(this.currentZoom);
        }

        return zoomLevels;
      }
    }
  },
  mounted() {
    this.config_.displayOnLoad = this.$options.mapBoxStyles[0].label;

    this.config_ = {
      ...this.config_,
      ...this.config
    };

    this.currentZoom = this.config_.zoom;
    this.currentTileLayer = this.config_.displayOnLoad;

    this.configForm = JSON.parse(JSON.stringify(this.config_));

    this.loadMap();
  },
  watch: {
    config: {
      handler: function(config) {
        this.config_ = {
          ...this.config_,
          ...this.config
        };
      },
      deep: true
    },
    "config_.center.lat"() {
      this.$nextTick(() => {
        this.resetView();
      });
    },
    "config_.center.lng"() {
      this.$nextTick(() => {
        this.resetView();
      });
    },
    "config_.zoom"(zoom) {
      this.map.setZoom(zoom);
    },
    "config_.zoomSnap"(zoomSnap) {
      this.map.options.zoomSnap = zoomSnap;
      this.map.options.zoomDelta = zoomSnap;
    },
    "config_.minZoom"(minZoom) {
      this.map.setMinZoom(minZoom);
    },
    "config_.maxZoom"(maxZoom) {
      this.map.setMaxZoom(maxZoom);
    },
    "config_.extendedBounds"(bounds) {
      this.setOfflineBounds();
    },
    "config_.offlineDownload"(offlineDownload) {
      this.$emit("update:offlineDownload", offlineDownload);
    },
    "config_.offlineZoomLevels"() {
      this.offlineControl.setZoomlevels(this.offlineZoomLevels);
    },
    "config_.scrollable"(scrollable) {
      if (scrollable) {
        this.map.boxZoom.enable();
        this.map.doubleClickZoom.enable();
        this.map.keyboard.enable();
        this.map.scrollWheelZoom.enable();
        this.map.touchZoom.enable();
        this.map.zoomControl.enable();
        this.map.dragging.enable();
      } else {
        this.map.boxZoom.disable();
        this.map.doubleClickZoom.disable();
        this.map.keyboard.disable();
        this.map.scrollWheelZoom.disable();
        this.map.touchZoom.disable();
        this.map.zoomControl.disable();
        this.map.dragging.disable();
      }
    },
    "config_.scrollWheelZoom"(scrollWheelZoom) {
      if (scrollWheelZoom) {
        this.map.scrollWheelZoom.enable();
      } else {
        this.map.scrollWheelZoom.disable();
      }
    },
    "config_.hasDrawControl"(hasDrawControl) {
      // Prevent external toggle if draw toggle is present
      if (this.config._hasDrawToggleControl) {
        if (hasDrawControl) {
          this.map.addControl(this.drawControl);
        } else {
          this.map.removeControl(this.drawControl);
        }
      }
    },
    "config_.hasDrawToggleControl"(hasDrawToggleControl) {
      if (hasDrawToggleControl) {
        this.map.addControl(this.drawToggleControl);
      } else {
        this.map.removeControl(this.drawToggleControl);
      }
    },
    "config_.hasOfflineControl"(hasOfflineControl) {
      // Hide control instead of removing it from the map completely so that offline download will still be functioning
      const offlineControlElem = document.getElementsByClassName(
        "savetiles leaflet-bar leaflet-control"
      );

      if (hasOfflineControl) {
        // this.map.addControl(this.offlineControl);
        offlineControlElem[offlineControlElem.length - 1].style.display =
          "block";
      } else {
        // this.map.removeControl(this.offlineControl);
        offlineControlElem[offlineControlElem.length - 1].style.display =
          "none";
      }
    },
    "config_.hasZoomControl"(hasZoomControl) {
      if (hasZoomControl) {
        this.map.addControl(this.map.zoomControl);
      } else {
        this.map.removeControl(this.map.zoomControl);
      }
    },
    "config_.hasLocateControl"(hasLocateControl) {
      if (hasLocateControl) {
        this.map.addControl(this.locateControl);
      } else {
        this.map.removeControl(this.locateControl);
      }
    },
    "config_.hasLayersControl"(hasLayersControl) {
      if (hasLayersControl) {
        this.map.addControl(this.layersControl);
      } else {
        this.map.removeControl(this.layersControl);
      }
    },
    drawing(drawing) {
      if (this.config_.hasDrawControl) {
        if (drawing) {
          this.map.addControl(this.drawControl);
          this.$emit("draw-start");
        } else {
          this.map.removeControl(this.drawControl);
          this.$emit("draw-cancel");
        }
      }
    }
  },
  methods: {
    loadGeoJson(
      geojson,
      featureId,
      style = null,
      activeStyle = null,
      popup = null
    ) {
      const options = {
        rendererFactory: L.svg.tile,
        vectorTileLayerStyles: {
          sliced: style || this.$options.vectorGridStyles.default
        },
        maxZoom: 19,
        interactive: true,
        getFeatureId: function(feature) {
          return feature.properties[featureId];
        }
      };

      const vectorGrid = L.vectorGrid.slicer(geojson, options);

      if (this.config_.clickable) {
        vectorGrid.on("click", e => {
          L.DomEvent.stopPropagation(e);
          let popup_ = null;
          let properties = e.layer.properties;

          if (popup) {
            popup_ = {
              content: popup(properties),
              latLng: e.latlng
            };
          }

          this.clickFeature(properties[featureId], activeStyle, popup_, e);
        });
      }

      if (popup) {
        vectorGrid
          .on("mouseover", e => {
            const properties = e.layer.properties;

            if (properties[featureId] !== this.activeFeature) {
              if (this.hoverFeaturePopup) {
                this.hoverFeaturePopup
                  .setContent(popup(properties))
                  .setLatLng(e.latlng);

                if (!this.hoverFeaturePopup.isOpen()) {
                  this.hoverFeaturePopup.openOn(this.map);
                }
              } else {
                this.hoverFeaturePopup = L.popup({
                  autoPan: false,
                  closeButton: false
                })
                  .setContent(popup(properties))
                  .setLatLng(e.latlng)
                  .openOn(this.map);
              }
            }
          })
          .on("mouseout", e => {
            if (this.hoverFeaturePopup) {
              this.hoverFeaturePopup.remove();
            }
          });
      }

      vectorGrid.addTo(this.map);

      this.layers.vectorGrids.push(vectorGrid);

      return this.layers.vectorGrids.length - 1;
    },
    clickFeature(id, activeStyle = null, popup = null, event) {
      if (this.activeFeature === id) {
        this.clearActiveFeature();
      } else {
        this.setActiveFeature(id, activeStyle);
      }

      this.$emit("click-feature", id, event);

      if (this.activeFeature) {
        if (popup) {
          if (this.activeFeaturePopup) {
            this.activeFeaturePopup
              .setContent(popup.content)
              .setLatLng(popup.latLng);

            if (!this.activeFeaturePopup.isOpen()) {
              this.activeFeaturePopup.openOn(this.map);
            }
          } else {
            this.activeFeaturePopup = L.popup({
              autoPan: false,
              autoClose: false,
              closeOnClick: false
            })
              .setContent(popup.content)
              .setLatLng(popup.latLng)
              .openOn(this.map);
          }
        }
      } else {
        if (this.activeFeaturePopup) {
          this.activeFeaturePopup.remove();
        }
      }
    },
    setActiveFeature(id, style = null) {
      this.clearActiveFeature();
      this.layers.vectorGrids.forEach(vectorGrid => {
        vectorGrid.setFeatureStyle(
          id,
          style || this.$options.vectorGridStyles.active
        );
        vectorGrid.redraw();
      });
      this.activeFeature = id;
    },
    clearActiveFeature() {
      if (this.activeFeature) {
        this.layers.vectorGrids.forEach(vectorGrid => {
          vectorGrid.resetFeatureStyle(this.activeFeature);
        });
        this.activeFeature = null;
      }
    },
    resetActiveFeaturePopup() {
      if (this.activeFeaturePopup) {
        this.activeFeaturePopup.remove();
        this.activeFeaturePopup.openOn(this.map);
      }
    },
    // This will add all vector grid back to the map
    addVectorGridsToMap(exclude = []) {
      this.layers.vectorGrids.forEach((vectorGrid, index) => {
        if (!exclude.includes(index)) {
          vectorGrid.addTo(this.map);
        }
      });
    },
    // This will remove all vector grid from the map
    removeVectorGridsToMap(exclude = []) {
      this.layers.vectorGrids.forEach((vectorGrid, index) => {
        if (!exclude.includes(index)) {
          vectorGrid.remove();
        }
      });
    },
    // This will permanently remove the vector grid
    removeVectorGrid(index) {
      this.layers.vectorGrids[index].remove();
      this.layers.vectorGrids.splice(index, 1);
    },
    // For now, vector grid doesn't have a method to soft redraw (no tile reload required)
    restyleVectorGrids(style, exclude = []) {
      this.layers.vectorGrids.forEach((vectorGrid, index) => {
        if (!exclude.includes(index)) {
          vectorGrid.options.vectorTileLayerStyles.sliced = style;
          vectorGrid.redraw();
        }
      });
    },
    toggleDrawing() {
      this.drawing = !this.drawing;

      L.DomUtil.setClass(
        this.icon,
        `fa fa-lg ${this.drawing ? "fa-times" : "fa-pencil"}`
      );
    },
    loadMap() {
      const vm = this;
      this.loadTileLayers();

      this.map = new L.Map(this.id, {
        renderer: L.canvas(),
        center: L.latLng(this.config_.center.lat, this.config_.center.lng),
        zoom: this.config_.zoom,
        zoomSnap: this.config_.zoomSnap,
        zoomDelta: this.config_.zoomSnap,
        minZoom: this.config_.minZoom,
        maxZoom: this.config_.maxZoom,
        layers: [this.defaultTileLayer]
      });

      this.map.whenReady(function() {
        vm.$emit('ready', vm.map);
      });

      this.resetView();

      /**
       * Fix - Broken Marker Image Urls
       */
      delete L.Icon.Default.prototype._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
        iconUrl: require("leaflet/dist/images/marker-icon.png"),
        shadowUrl: require("leaflet/dist/images/marker-shadow.png")
      });

      /**
       * Map Screenshoter
       */
      this.mapScreenshoter = L.simpleMapScreenshoter({
        hidden: true
      }).addTo(this.map);

      /**
       * Map Layers
       */
      this.layers.main = L.featureGroup().addTo(this.map);
      this.layers.me = L.featureGroup().addTo(this.layers.main);
      this.layers.markers = L.featureGroup().addTo(this.layers.main);
      this.layers.circles = L.featureGroup().addTo(this.layers.main);
      this.layers.lines = L.featureGroup().addTo(this.layers.main);
      this.layers.polygons = L.featureGroup().addTo(this.layers.main);

      /**
       * Map Icons
       * @todo - Remove asset icons
       */
      this.icons = {
        default: L.divIcon({
          className: "custom-marker",
          // iconAnchor: [10, 20],
          iconAnchor: [10, 15],
          iconSize: [50, 50],
          popupAnchor: [0, -30],
          html: `<div class="marker" style="background: ${this.$options.colors.marker}"></div>`
        }),
        CPIT: new L.Icon({
          iconUrl: require("@/assets/map/cpit.png"),
          iconSize: [17, 13],
          iconAnchor: [7, 12],
          popupAnchor: [0, -7]
        }),
        ILOL: new L.Icon({
          iconUrl: require("@/assets/map/ilol.png"),
          iconSize: [10, 10],
          iconAnchor: [7, 12],
          popupAnchor: [0, -7]
        }),
        MHCR: new L.Icon({
          iconUrl: require("@/assets/map/mhcr.png"),
          iconSize: [15, 15],
          iconAnchor: [7, 14],
          popupAnchor: [0, -7]
        }),
        SOAK: new L.Icon({
          iconUrl: require("@/assets/map/soak.png"),
          iconSize: [14, 14],
          iconAnchor: [7, 14],
          popupAnchor: [0, -7]
        }),
        WRTD: new L.Icon({
          iconUrl: require("@/assets/map/wrtd.png"),
          iconSize: [17, 15],
          iconAnchor: [8, 16],
          popupAnchor: [0, -7]
        })
      };

      /**
       * Layers Control
       */
      this.layersControl = new L.control.layers(
        this.tileLayers,
        {},
        { position: "bottomright" }
      ).addTo(this.map);

      /**
       * Draw Control
       */
      L.drawLocal.draw.toolbar.buttons.marker = "Draw a point";

      this.drawControl = new L.Control.Draw({
        position: "topright",
        draw: {
          marker: {
            shapeOptions: {
              renderer: L.svg()
            }
          },
          polyline: {
            shapeOptions: {
              renderer: L.svg()
            }
          },
          polygon: {
            shapeOptions: {
              renderer: L.svg()
            }
          },
          rectangle: false,
          circle: false,
          circlemarker: false
        }
      });

      /**
       * Draw Toggle Control
       */
      L.Control.DrawToggle = L.Control.extend({
        onAdd: () => {
          const container = L.DomUtil.create("div", "leaflet-bar");
          const btn = L.DomUtil.create("a", "draw-btn", container);

          // Store icon to data so we can modify its class externally
          this.icon = L.DomUtil.create("i", "fa fa-pencil fa-lg", btn);

          btn.style = "color:	#3D4849";

          L.DomEvent.on(btn, "click", (e) => {
            L.DomEvent.stopPropagation(e);
            this.toggleDrawing();
          });

          return container;
        },
        onRemove: () => {}
      });

      this.drawToggleControl = new L.Control.DrawToggle({
        position: "topright"
      });

      /**
       * Locate Control
       */
      L.Control.Locate = L.Control.extend({
        onAdd: () => {
          const container = L.DomUtil.create("div", "leaflet-bar");
          const btn = L.DomUtil.create("a", "", container);
          const icon = L.DomUtil.create("i", "fa fa-map-marker fa-lg", btn);

          btn.style = "color:	#048abf";

          L.DomEvent.on(btn, "click", (e) => {
            L.DomEvent.stopPropagation(e);
            this.map.locate({ setView: true });
          });

          return container;
        },
        onRemove: () => {}
      });

      this.locateControl = new L.Control.Locate({
        position: "bottomleft"
      }).addTo(this.map);

      /**
       * Fix - callback is not a function error inside ControlSaveTiles setStorageSize function.
       * Occurs when there's more than two leafletmap initialized.
       */
      if (!window._offlineControl) {
        window._offlineControl = L.control.savetiles(this.defaultTileLayer, {});
      }

      window._offlineControl.status.storagesize = 0;

      /**
       * Offline Control
       */
      this.offlineControl = L.control.savetiles(this.defaultTileLayer, {
        zoomlevels: this.offlineZoomLevels,
        saveText:
          '<i class="fa fa-download has-text-grey-dark" aria-hidden="true"></i>',
        rmText:
          '<i class="fa fa-trash has-text-danger" aria-hidden="true"></i>',
        confirm: (status, confirmCallback) => {
          // Prevent calling the leaflet.offline's successCallback
        },
        confirmRemoval: (status, confirmRemovalCallback) => {
          if (
            window.confirm("Do you want to clear the downloaded map tiles?")
          ) {
            confirmRemovalCallback();
          }
        },
        position: "bottomleft"
      });

      this.offlineControl.addTo(this.map);

      // Manually attach on click event to offline download button
      const offlineDownloadBtn = document.querySelector("a.savetiles");

      offlineDownloadBtn.addEventListener("click", () => {
        if (confirm("Do you want to save the current map tiles?")) {
          this.downloadTiles(true);
        }
      });

      if (this.config_.hasCustomControl) {
        L.Control.CustomControl = L.Control.extend({
          onAdd: () => {
            const container = L.DomUtil.create("div", "leaflet-bar");
            const btn = L.DomUtil.create("a", "", container);
            const icon = L.DomUtil.create(
              "i",
              this.config_.customControlIcon,
              btn
            );

            btn.style = "color:	#3D4849";

            L.DomEvent.on(btn, "click", () => {
              this.$emit("click-custom-control");
            });

            return container;
          },
          onRemove: () => {}
        });

        new L.Control.CustomControl({
          position: this.config_.customControlPosition
        }).addTo(this.map);
      }

      /**
       * Map Events
       */
      this.map.on("click", event => {
        this.$emit("click-map", event);

        this.setMarkerOnClick(event.latlng);
      });

      this.map.on("locationfound", event => {
        this.$emit("locationfound", event);

        if (!this.me.marker) {
          // this.me.marker = this.loadObject(
          //   { latLng: event.latlng },
          //   "circleMarker",
          //   "circle",
          //   "me",
          //   {
          //     radius: 10,
          //     weight: 5,
          //     color: "#FFF",
          //     fillColor: "#222",
          //     fillOpacity: 1
          //   },
          //   "fillColor",
          //   this.layers.me
          // );
          // this.me.radius = this.loadObject(
          //   { latLng: event.latlng },
          //   "circle",
          //   "circle",
          //   "me",
          //   {
          //     radius: 700,
          //     weight: 1,
          //     color: "#222",
          //     fillColor: "#3388ff",
          //     fillOpacity: 0.2
          //   },
          //   "fillColor",
          //   this.layers.me
          // );
        } else {
          // this.me.marker.setLatLng(event.latlng);
          // this.me.radius.setLatLng(event.latlng);
        }

        this.setMarkerOnClick(event.latlng);
      });

      this.map.on("draw:created", event => {
        this.$emit("draw-created", event);
      });

      this.map.on("moveend", event => {
        this.setOfflineBounds();
        this.setExtendedBounds();
        this.$emit("move-end", event);
      });

      this.map.on("zoomend", () => {
        this.currentZoom = this.map.getZoom();
        this.offlineControl.setZoomlevels(this.offlineZoomLevels);
        this.$emit("zoom-end", event);
      });

      this.map.on("baselayerchange", event => {
        this.tileLayers[this.currentTileLayer].remove();
        this.currentTileLayer = event.name;
        this.offlineControl.setLayer(this.tileLayers[this.currentTileLayer]);
        this.downloadTiles();

        // Vector grid layers disappears when changing base layer
        this.layers.vectorGrids.forEach(vectorGrid => {
          vectorGrid.bringToFront();
        });
      });

      Object.keys(this.tileLayers).forEach(key => {
        this.tileLayers[key].on("savetileend", status => {
          this.pendingTilesCount--;
          this.downloadedTilesCount = status.lengthSaved;
          this.totalTilesCount = status.lengthToBeSaved;
          this.downloading = true;
        });
        this.tileLayers[key].on("saveend", status => {
          this.downloading = false;
        });
      });
    },
    loadTileLayers() {
      this.$options.mapBoxStyles.forEach(style => {
        this.tileLayers[style.label] = new L.tileLayer.offline(
          this.$options.mapBoxUrl,
          {
            attribution: this.$options.mapBoxAttribution,
            id: `${style.id}`,
            accessToken: process.env.LEAFLET_ACCESS_TOKEN,
            minZoom: 0,
            maxZoom: 19
          }
        );
      });
    },
    // Makes sure that the map is fitted to its container
    // Resets map's view/center using the center config if config parameter is set to true
    resetView(config = true) {
      if (config) {
        this.map.setView(
          L.latLng(this.config_.center.lat, this.config_.center.lng)
        );
      }

      setTimeout(() => {
        this.map.invalidateSize();
      }, 500);
    },
    clearLayer(layer) {
      this.layers[layer].clearLayers();
    },
    loadMarkers(key = "") {
      if (key) {
        const keys = [];

        // Only load unloaded markers
        this.markers.forEach(marker => {
          const loadedIndex = this.loadedMarkers.findIndex(
            loaded => loaded.options[key] === marker.options[key]
          );

          if (loadedIndex < 0) {
            console.log("Marker not loaded.");
            this.loadedMarkers.push(this.loadMarker(marker));
          }

          keys.push(marker.options[key]);
        });

        // Sync loaded markers and markers
        // Unload loaded markers that aren't in the markers array
        this.loadedMarkers = this.loadedMarkers.filter(loaded => {
          if (keys.includes(loaded.options[key])) {
            return true;
          }

          this.layers.markers.removeLayer(loaded);

          return false;
        });

        console.log("Loaded markers", this.loadedMarkers.length);
      } else {
        this.markers.forEach(marker => {
          this.loadMarker(marker);
        });
      }
    },
    loadMarker(marker) {
      let icon = null;

      if (marker.icon) {
        if (marker.icon.custom) {
          icon = this.icons[marker.icon.name];
        } else {
          icon = new L.divIcon({
            className: "custom-marker",
            // iconAnchor: [10, 20],
            iconAnchor: [10, 15],
            iconSize: [50, 50],
            popupAnchor: [0, -30],
            html: `<div class="marker" style="background: ${marker.icon.color ||
              this.$options.colors.marker};${marker.icon.style ||
              ""}"><div class="text" style="color: ${marker.icon.textColor ||
              "#fff"}">${marker.icon.text || ""}</div></div>`
          });
        }
      }

      return this.loadObject(marker, "marker", "marker", "markers", {
        icon: icon || this.icons.default
      });
    },
    loadLines(key = "") {
      if (key) {
        const keys = [];

        // Only load unloaded lines
        this.lines.forEach(line => {
          const loadedIndex = this.loadedLines.findIndex(
            loaded => loaded.options[key] === line.options[key]
          );

          if (loadedIndex < 0) {
            console.log("Line not loaded.");
            this.loadedLines.push(this.loadLine(line));
          }

          keys.push(line.options[key]);
        });

        // Sync loaded lines and lines
        // Unload loaded lines that aren't in the lines array
        this.loadedLines = this.loadedLines.filter(loaded => {
          if (keys.includes(loaded.options[key])) {
            return true;
          }

          this.layers.lines.removeLayer(loaded);

          return false;
        });

        console.log("Loaded lines", this.loadedLines.length);
      } else {
        this.lines.forEach(line => {
          this.loadLine(line);
        });
      }
    },
    loadLine(line) {
      return this.loadObject(line, "polyline", "line", "lines", {
        color: this.$options.colors.line
      });
    },
    loadPolygons(key = "") {
      if (key) {
        const keys = [];

        // Only load unloaded polygons
        this.polygons.forEach(polygon => {
          const loadedIndex = this.loadedPolygons.findIndex(
            loaded => loaded.options[key] === polygon.options[key]
          );

          if (loadedIndex < 0) {
            console.log("Polygon not loaded.");
            this.loadedPolygons.push(this.loadPolygon(polygon));
          }

          keys.push(polygon.options[key]);
        });

        // Sync loaded polygons and polygons
        // Unload loaded polygons that aren't in the polygons array
        this.loadedPolygons = this.loadedPolygons.filter(loaded => {
          if (keys.includes(loaded.options[key])) {
            return true;
          }

          this.layers.polygons.removeLayer(loaded);

          return false;
        });

        console.log("Loaded polygons", this.loadedPolygons.length);
      } else {
        this.polygons.forEach(polygon => {
          this.loadPolygon(polygon);
        });
      }
    },
    loadPolygon(polygon) {
      return this.loadObject(polygon, "polygon", "polygon", "polygons", {
        color: this.$options.colors.polygons
      });
    },
    loadCircles(key = "") {
      if (key) {
        const keys = [];

        // Only load unloaded circles
        this.circles.forEach(circle => {
          const loadedIndex = this.loadedCircles.findIndex(
            loaded => loaded.options[key] === circle.options[key]
          );

          if (loadedIndex < 0) {
            console.log("Circle not loaded.");
            this.loadedCircles.push(this.loadCircle(circle));
          }

          keys.push(circle.options[key]);
        });

        // Sync loaded circles and circles
        // Unload loaded circles that aren't in the circles array
        this.loadedCircles = this.loadedCircles.filter(loaded => {
          if (keys.includes(loaded.options[key])) {
            return true;
          }

          this.layers.circles.removeLayer(loaded);

          return false;
        });

        console.log("Loaded circles", this.loadedCircles.length);
      } else {
        this.circles.forEach(circle => {
          this.loadCircle(circle);
        });
      }
    },
    loadCircle(circle) {
      let options = {
        radius: 5,
        stroke: true,
        color: "black",
        opacity: 1,
        weight: 1,
        fill: true,
        fillColor: this.$options.colors.circle,
        fillOpacity: 1
      };

      return this.loadObject(
        circle,
        "circle",
        "circle",
        "circles",
        options,
        "fillColor"
      );
    },
    loadObject(obj, ltype, type, layer, options = {}, colorAttr = "color") {
      if (obj.options) {
        options = { ...options, ...obj.options, bubblingMouseEvents: false };
      }

      let lobj = L[ltype](
        obj[`${type === "circle" || type === "marker" ? "latLng" : "latLngs"}`],
        options
      );

      this.layers[layer].addLayer(lobj);

      if (obj.active) {
        this.setActive(lobj, type, colorAttr);
      }

      if (!obj.disabled) {
        lobj.on("click", event => {
          this.setActive(lobj, type, colorAttr);
          this.$emit(`click-${type}`, event);
        });
      }

      if (obj.popup && obj.popup.content) {
        this.bindPopup(lobj, obj.popup, obj.active);
      }

      return lobj;
    },
    replaceObject() {}, // TODO
    setActive(obj, type, colorAttr) {
      let options = {};

      if (this.previousActive.obj) {
        options[this.previousActive.colorAttr] = this.previousActive.color;
        this.previousActive.obj.setStyle(options);
      }

      if (type != "marker") {
        this.previousActive.obj = obj;
        this.previousActive.color = obj.options[colorAttr];
        this.previousActive.colorAttr = colorAttr;

        options = {};
        options[colorAttr] = this.$options.colors.active;

        obj.setStyle(options);
      } else {
        this.previousActive.obj = null;
      }
    },
    bindPopup(lobj, popup, active = false) {
      lobj.bindPopup(popup.content, {
        autoPan: false,
        closeButton: !popup.hoverable,
        closeOnEscapeKey: false,
        closeOnClick: false,
        ...popup.options
      });

      if (popup.hoverable) {
        lobj.on("mouseover", () => {
          lobj.openPopup();
        });
        lobj.on("mouseout", () => {
          lobj.closePopup();
        });
      }

      if (active) {
        lobj.openPopup();
      }

      return lobj;
    },
    fitMainLayerBounds() {
      // Check for valid bounds else error occurrs.
      const bounds = this.layers.main.getBounds();
      if (bounds.isValid()) {
        this.map.fitBounds(this.layers.main.getBounds());
        setTimeout(() => {
          this.map.fitBounds(this.layers.main.getBounds());
        }, 500);
      }
    },
    async downloadTiles(manual = false) {
      if ((manual || this.config_.offlineDownload) && navigator.onLine) {
        const downloadedTiles = await offlineStorage.keys();

        this.offlineControl._saveTiles();

        let tilesForSave = [];
        let newTiles = this.offlineControl.status._tilesforSave || [];

        newTiles.forEach(tile => {
          if (
            !downloadedTiles.includes(tile.key) &&
            !this.pendingTileRequests.includes(tile.key)
          ) {
            tilesForSave.push(tile);
            this.loadTile(tile);
          }
        });

        this.offlineControl.status._tilesforSave = tilesForSave;
        this.offlineControl.status.lengthToBeSaved = this.pendingTilesCount;
      }
    },
    loadTile(tile) {
      this.pendingTileRequests.push(tile.key);
      this.pendingTilesCount++;

      let xhr = new XMLHttpRequest();

      xhr.open("GET", tile.url);
      xhr.responseType = "blob";
      xhr.send();
      xhr.onreadystatechange = () => {
        if (
          xhr.readyState === XMLHttpRequest.DONE &&
          xhr.status >= 200 &&
          xhr.status <= 300
        ) {
          this.offlineControl._saveTile(tile.key, xhr.response);

          const index = this.pendingTileRequests.findIndex(
            req => req === tile.key
          );

          if (index > -1) {
            this.pendingTileRequests.splice(index, 1);
          }

          if (this.pendingTileRequests.length > 0) {
            this.offlineControl._baseLayer.fire("loadtileend", self.status);
          } else {
            this.offlineControl._baseLayer.fire("loadtileend", self.status);
          }
        }
      };
    },
    applyConfig() {
      this.config_ = JSON.parse(JSON.stringify(this.configForm));

      this.defaultTileLayer.addTo(this.map);
      this.map.setZoom(this.config_.zoom);
      this.resetView();

      this.$emit("update:config", this.config_);
    },
    takeScreenshot() {
      return this.mapScreenshoter.takeScreen("image").then(image => {
        return image;
      });
    },
    setExtendedBounds() {
      if (!this.extendedBounds) {
        this.extendedBounds = this.getExtendedBounds();
      }

      // If the current map bounds reaches the end of the extended bounds, set new extended bounds
      if (!this.extendedBounds.contains(this.map.getBounds())) {
        this.extendedBounds = this.getExtendedBounds();
        this.$emit("border-reached", this.map.getCenter());
      }
    },
    setOfflineBounds() {
      const extendedBounds = this.getExtendedBounds();

      this.offlineControl.setBounds(extendedBounds);

      // Proximity filter will be used instead
      // this.$store.commit("AssetsMap/SET_STATE", {
      //   state: "maxRadius",
      //   value: this.calculateRadius(extendedBounds)
      // });

      this.$nextTick(() => {
        this.downloadTiles();
      });
    },
    getExtendedBounds() {
      const bounds = this.map.getBounds();

      // [2020-01-17]
      // Performance: we're no longer using extended bounds.
      return bounds;

      const extendedTileSize =
        this.$options.tileSize * (this.config_.extendedBounds / 100);

      let northEast = this.map.project(bounds.getNorthEast(), this.currentZoom);
      let southWest = this.map.project(bounds.getSouthWest(), this.currentZoom);

      northEast = northEast.add(L.point(extendedTileSize, -extendedTileSize));
      southWest = southWest.add(L.point(-extendedTileSize, extendedTileSize));

      return L.latLngBounds(
        this.map.unproject(northEast),
        this.map.unproject(southWest)
      );
    },
    calculateRadius(bounds) {
      // 0 - Northeast, 1 - Southwest
      const keys = Object.keys(bounds);

      // Determine whether x or y will be used as the radius
      const distanceX = this.calculateDistance(
        bounds[keys[0]].lng,
        bounds[keys[0]].lat,
        bounds[keys[1]].lng,
        bounds[keys[0]].lat
      );
      const distanceY = this.calculateDistance(
        bounds[keys[1]].lng,
        bounds[keys[0]].lat,
        bounds[keys[1]].lng,
        bounds[keys[1]].lat
      );

      return (distanceX > distanceY ? distanceX : distanceY) / 2;
    },
    calculateDistance(p1x, p1y, p2x, p2y) {
      return (
        6371 *
        Math.acos(
          Math.cos(this.degToRad(p1y)) *
            Math.cos(this.degToRad(p2y)) *
            Math.cos(this.degToRad(p2x) - this.degToRad(p1x)) +
            Math.sin(this.degToRad(p1y)) * Math.sin(this.degToRad(p2y))
        )
      );
    },
    degToRad(degrees) {
      return degrees * (Math.PI / 180);
    },
    setMarkerOnClick(latlng) {
      if (!this.config_.markerOnClick) return;

      // Clear marker if latlng is null
      if (!latlng) {
        if (this.flexible.marker) {
          this.layers.main.removeLayer(this.flexible.marker);
          this.flexible.marker = null;
        }
        return;
      }

      if (this.flexible.marker) {
        this.flexible.marker.setLatLng(latlng);
      } else {
        this.flexible.marker = this.loadObject(
          { latLng: latlng },
          "marker",
          "marker",
          "main",
        );
      }
    }
  }
};
</script>

<style lang="scss">
.leaflet-offline-map {
  .leaflet-container {
    z-index: 1;
  }
  .marker {
    width: 30px;
    height: 30px;
    border-radius: 50% 50% 50% 0;
    border: 1.3px #2c3e50 solid;
    position: absolute;
    transform: rotate(-45deg);
    // left: 50%;
    // top: 50%;
    transform-origin: bottom left;
    left: 80%;
    top: 61%;
    margin: -43px 0 0 -30px;

    .text {
      margin-top: 5px;
      text-align: center;
      transform: rotate(45deg);
    }
  }
  .map {
    position: relative;

    .progress-bar {
      position: absolute;
      top: 1vh;
      z-index: 1;
      width: 100%;

      .progress {
        width: 70%;
        margin: auto;
      }
    }
  }
}
.leaflet-offline-map.column-reversed {
  flex-direction: column-reverse;
}
.leaflet-offline-map.row-reversed {
  flex-direction: row-reverse;
}
</style>



// WEBPACK FOOTER //
// src/components/LeafletOfflineMap.vue